import { useState, useEffect, useRef } from 'react';
import { logEvent as diagLog } from '@/lib/diagnostics';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import Layout from '@/components/Layout';
import { MatchScoreBadge, ExplanationChips, DistanceMeta } from '@/components/MatchingUI';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RegionContextLine } from '@/components/jobs/RegionContextLine';
import { PinJobButton } from '@/components/DiagnosticBadge';
import { PageHeader } from '@/components/ui/PageHeader';
import { skillbridgeDisplay } from '@/lib/skillbridge';
import OpportunitiesMap from '@/components/opportunities/OpportunitiesMap';
import OpportunityPreview from '@/components/opportunities/OpportunityPreview';
import { List as ListIcon, Map as MapIcon, Columns2 } from 'lucide-react';
import { Search, MapPin, Briefcase, DollarSign, Heart, AlertCircle, Eye, SlidersHorizontal, ArrowUpDown, Info, Check, X, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Opportunities engine selection. DEFAULT = V2 (the accepted engine). Emergency rollback via
// /jobs?engine=v1 keeps the untouched V1 implementation reachable. Query params are preserved.
const ENGINE = (typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('engine') === 'v1') ? 'v1' : 'v2';
const JOBS_PATH = ENGINE === 'v2' ? '/api/opportunities-v2' : '/api/jobs';
const MATCH_STATUS_PATH = ENGINE === 'v2' ? '/api/match-v2/status' : '/api/jobs/match-status';

// Native select used inside the SkillBridge More Filters panel.
function SbSelect({ label, testid, value, onChange, options }) {
  return (
    <div>
      <Label className="text-slate-700 text-sm">{label}</Label>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 h-10 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand/30">
        <option value="">Any</option>
        {(options || []).map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

// Absolute Match (0-100). Displayed as a whole number.
function formatMatch(m) {
  if (m == null) return null;
  return String(Math.round(m));
}

function MatchInfo() {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="match-info-btn"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center justify-center text-slate-400 hover:text-brand transition-colors"
          aria-label="What is Match?"
        >
          <Info className="w-3.5 h-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 text-xs leading-relaxed" onClick={(e) => e.stopPropagation()} data-testid="match-info-popover">
        <p className="font-semibold text-slate-900 mb-1.5">What is Match?</p>
        <p className="text-slate-600">
          Match is an absolute, evidence-based score (0–100) of how well this specific opportunity fits your
          background — based on your skills, knowledge and abilities, military occupation, certifications and
          education, leadership, and civilian experience. It is not scored against other opportunities and does
          not factor in location. It is not the percentage of qualifications you meet and is not a prediction of
          whether you will be selected. Higher means stronger evidence of fit.
        </p>
      </PopoverContent>
    </Popover>
  );
}

function JobBoard() {
  const [user, setUser] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [favorites, setFavorites] = useState(new Set());
  const EMPTY_SB = { career_field:'', program_type:'', duration:'', installation:'', provider:'', state:'', branches:[] };
  const [filters, setFilters] = useState({
    keyword: '', location: '', remote: '', job_type: '', salary_min: '', radius_miles: '50', opportunity_type: '', saved_only: false, sb: { ...EMPTY_SB }
  });
  const [sortBy, setSortBy] = useState('best_match');
  const [loading, setLoading] = useState(true);
  const [resumeStatus, setResumeStatus] = useState(null);
  // Canonical Match readiness — SAME condition as the "Complete your resume…" banner.
  // Gates all personalized-Match UI (badges, "Matched", Best Match) so a not-ready seeker
  // never sees scores, even if stale client state briefly holds them.
  const matchReady = !!resumeStatus?.is_complete;
  const [resume, setResume] = useState(null);
  // Opportunity Preview (Zillow-style drawer/bottom sheet) — opened from map pins/stack rows.
  const [preview, setPreview] = useState(null);
  // Bumped when a favorite changes while Saved mode is active, to re-query the saved map live.
  const [savedNonce, setSavedNonce] = useState(0);
  const [homeLocation, setHomeLocation] = useState(null);
  // Location Anchor: 'base' | 'home' | 'other' | 'anywhere'
  const [locationAnchor, setLocationAnchor] = useState('anywhere');
  const [locOpen, setLocOpen] = useState(false);
  const [locOpenM, setLocOpenM] = useState(false);
  const [genMoreOpen, setGenMoreOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [otherDraft, setOtherDraft] = useState('');
  const [otherCoords, setOtherCoords] = useState(null); // session-only temp coords for "Another location"
  const [homeCoords, setHomeCoords] = useState(null); // {lat, lng} or null
  const [baseCoords, setBaseCoords] = useState(null); // {lat, lng} or null
  // True when a seeker has a resume but NO valid Base/Home coordinates — we transparently
  // fall back to "Anywhere" AND tell the user (never silently pretend to have a location).
  const [locationFallback, setLocationFallback] = useState(false);
  const navigate = useNavigate();
  // DIAGNOSTICS ONLY (no-op unless ?diag=1): stable per-mount id + mount/unmount lifecycle markers
  // to prove whether remounting contributes to the observed automatic-rescore trigger.
  const diagInstanceId = useRef(
    (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`
  ).current;
  useEffect(() => {
    diagLog('opportunities_component_mounted', { component_instance_id: diagInstanceId, engine: ENGINE });
    return () => diagLog('opportunities_component_unmounted', { component_instance_id: diagInstanceId });
  }, []);
  const isScored = matchReady && jobs[0]?.match_score != null;

  const PAGE_SIZE = 20;
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [viewMode, setViewMode] = useState(() => {
    const mobile = (typeof window !== 'undefined' && window.innerWidth < 768);
    let stored = null;
    try { stored = sessionStorage.getItem('opps_view_mode_v1'); } catch {}
    const valid = mobile ? ['list', 'map'] : ['list', 'split', 'map'];
    if (stored && valid.includes(stored)) return stored;
    return mobile ? 'list' : 'split';
  });
  // Persist the selected List/Split/Map view for THIS browser session (sessionStorage, not account).
  useEffect(() => {
    try { sessionStorage.setItem('opps_view_mode_v1', viewMode); } catch {}
  }, [viewMode]);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const cardRefs = useRef({});
  useEffect(() => {
    if (selectedJobId && cardRefs.current[selectedJobId]) {
      cardRefs.current[selectedJobId].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selectedJobId]);

  // One canonical in-flight jobs request at a time: abort obsolete requests, ignore
  // stale responses, and dedupe identical concurrent requests. No transient generic call.
  const abortRef = useRef(null);
  const reqSeqRef = useRef(0);
  const inFlightUrlRef = useRef(null);
  // Dedup guard for the opportunities_viewed telemetry: one emit per genuinely
  // completed first-page presentation. Same-URL completions (silent revalidation,
  // duplicate identical requests) and snapshot restores never re-emit.
  const lastViewedUrlRef = useRef(null);
  const SNAP_KEY = 'opps_view_v1';
  // Independent short-TTL persistence of the last opportunities_viewed URL so the emit
  // is not double-counted across an unmount/remount (job detail → back, snapshot restore,
  // silent revalidation). Rehydrated on mount within a 2-min window.
  const LAST_VIEW_KEY = 'opps_last_view_v1';

  // ── SkillBridge "More Filters" ──
  const [sbOptions, setSbOptions] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [sbDraft, setSbDraft] = useState({ ...EMPTY_SB });
  const sbActiveCount = (s => (
    (s.career_field ? 1 : 0) + (s.program_type ? 1 : 0) + (s.duration ? 1 : 0) +
    (s.installation ? 1 : 0) + (s.provider ? 1 : 0) + (s.state ? 1 : 0) +
    (s.branches?.length ? 1 : 0)
  ))(filters.sb || EMPTY_SB);

  useEffect(() => {
    if (filters.opportunity_type === 'skillbridge' && !sbOptions) {
      axios.get(`${BACKEND_URL}/api/jobs/skillbridge/filter-options`, { withCredentials: true })
        .then(r => setSbOptions(r.data)).catch(() => {});
    }
  }, [filters.opportunity_type]);

  const openMoreFilters = () => { setSbDraft({ ...EMPTY_SB, ...(filters.sb || {}), branches: [...(filters.sb?.branches || [])] }); setMoreOpen(true); };
  const applySbFilters = () => {
    setMoreOpen(false);
    const sb = { ...sbDraft };
    emitEvent('opportunity_filter_changed', { filters_changed: ['skillbridge_filters'] });
    setFilters(prev => ({ ...prev, sb }));
    fetchFirstPage({ filters: { sb } });
  };
  const clearSbFilters = () => {
    setSbDraft({ ...EMPTY_SB });
    emitEvent('opportunity_filter_changed', { filters_changed: ['skillbridge_filters'] });
    setFilters(prev => ({ ...prev, sb: { ...EMPTY_SB } }));
    setMoreOpen(false);
    fetchFirstPage({ filters: { sb: { ...EMPTY_SB } } });
  };
  const toggleDraftBranch = (b) => setSbDraft(d => ({
    ...d, branches: d.branches.includes(b) ? d.branches.filter(x => x !== b) : [...d.branches, b]
  }));

  // True filtered/collapsed result count comes from the X-Total-Count response header.
  const readTotal = (res, fallback) => {
    const h = res?.headers?.['x-total-count'];
    const n = h != null ? parseInt(h, 10) : NaN;
    return Number.isFinite(n) ? n : fallback;
  };

  // Match readiness for the current filtered universe. 'preparing' = Best Match is being
  // materialized in the background; the list is shown in a fast non-Match browse order.
  const [matchState, setMatchState] = useState(null);
  const readMatchState = (res) => res?.headers?.['x-match-state'] || null;
  // V2-only: opportunities-v2 reports validated Home/Base coordinate availability via headers.
  const readLoc = (res) => ({ home: res?.headers?.['x-loc-home'] === '1',
                              base: res?.headers?.['x-loc-base'] === '1' });
  const [locAvail, setLocAvail] = useState({ home: true, base: true });

  const isValidCoord = (lat, lng) =>
    lat != null && lng != null && !(lat === 0 && lng === 0);

  const buildParams = (f, skipVal, anchor, coords, sortVal = sortBy) => {
    const params = new URLSearchParams();
    if (f.keyword) params.append('keyword', f.keyword);
    if (f.remote === 'true') params.append('remote', 'true');
    if (f.remote === 'false') params.append('remote', 'false');
    if (f.job_type) params.append('job_type', f.job_type);
    if (f.salary_min) params.append('salary_min', f.salary_min);
    if (f.opportunity_type) params.append('opportunity_type', f.opportunity_type);
    // SkillBridge secondary filters only apply while SkillBridge is the selected type.
    if (f.opportunity_type === 'skillbridge' && f.sb) {
      const s = f.sb;
      if (s.career_field) params.append('sb_career_field', s.career_field);
      if (s.program_type) params.append('sb_program_type', s.program_type);
      if (s.duration) params.append('sb_duration', s.duration);
      if (s.installation) params.append('sb_installation', s.installation);
      if (s.provider) params.append('sb_provider', s.provider);
      if (s.state) params.append('sb_state', s.state);
      if (s.branches && s.branches.length) params.append('sb_branch', s.branches.join(','));
    }
    // SAVED: top-level intersection filter (all types, authenticated seeker resolved server-side).
    if (f.saved_only) params.append('saved_only', 'true');
    if (sortVal) params.append('sort_by', sortVal);

    const unlimited = f.radius_miles === 'any';
    if (anchor === 'other') {
      // Ad-hoc "Another location": use the SAME resolved coordinates + radius as Home/Base so the
      // LIST filters geographically (not by location-name text) and matches the MAP's meaning.
      const c = coords && coords.other;
      if (c && !unlimited) {
        params.append('origin_lat', String(c.lat));
        params.append('origin_lng', String(c.lng));
        params.append('radius_miles', f.radius_miles || '50');
        params.append('distance_ref', 'search');
      }
      // unlimited 'other' → no geographic constraint (radius irrelevant).
    } else if ((anchor === 'base' || anchor === 'home') && !unlimited) {
      // Stored candidate coordinates — passed as already-resolved origin (no geocode).
      const c = anchor === 'base' ? coords.base : coords.home;
      if (c) {
        params.append('origin_lat', String(c.lat));
        params.append('origin_lng', String(c.lng));
        params.append('radius_miles', f.radius_miles || '50');
        params.append('distance_ref', anchor);
      }
    }
    // anchor === 'anywhere' → no location/radius/origin params (unrestricted)
    params.append('limit', String(PAGE_SIZE));
    params.append('skip', String(skipVal));
    return params;
  };

  // Snapshot the current view so returning to Opportunities restores instantly (PART 6).
  const stateRef = useRef({});
  stateRef.current = { jobs, total, filters, sortBy, locationAnchor, matchState, skip, hasMore };
  // Current seeker identity — used to scope the view snapshot so one seeker can never
  // visually inherit another seeker's cached Match results after logout/login.
  const uidRef = useRef(null);
  uidRef.current = user?.id || null;
  // Latest sortBy for the (single) polling loop, so a sort change never recreates the timer.
  const sortByRef = useRef(sortBy);
  sortByRef.current = sortBy;
  // Stalled-progress tracking (frontend only): detect when `scored` stops advancing.
  const progressRef = useRef({ scored: -1, ts: 0 });
  // Guards exactly ONE Best Match fetch per ready-edge. Reset whenever status is non-ready
  // (incl. after a reopen sends us back to preparing) so the NEXT true ready refetches once.
  const readyEdgeRef = useRef(false);
  const [matchStalled, setMatchStalled] = useState(false);
  // Delayed "Updating opportunities…" indicator for in-progress query changes.
  const [showUpdating, setShowUpdating] = useState(false);
  useEffect(() => {
    if (loading && jobs.length > 0) {
      const t = setTimeout(() => setShowUpdating(true), 250);
      return () => clearTimeout(t);
    }
    setShowUpdating(false);
  }, [loading, jobs.length]);
  useEffect(() => {
    return () => {
      try {
        sessionStorage.setItem(SNAP_KEY, JSON.stringify({
          ...stateRef.current, uid: uidRef.current, scrollY: window.scrollY, ts: Date.now(),
        }));
      } catch {}
    };
  }, []);

  const restoreSnapshot = (currentUid) => {
    try {
      const raw = sessionStorage.getItem(SNAP_KEY);
      if (!raw) return false;
      const s = JSON.parse(raw);
      if (!s || !Array.isArray(s.jobs) || s.jobs.length === 0) return false;
      if (Date.now() - (s.ts || 0) > 120000) return false; // >2min → refetch fresh
      // Account-switch isolation: never restore another seeker's (or anonymous) snapshot.
      if ((s.uid || null) !== (currentUid || null)) { sessionStorage.removeItem(SNAP_KEY); return false; }
      setJobs(s.jobs); setTotal(s.total); setFilters(s.filters); setSortBy(s.sortBy);
      setLocationAnchor(s.locationAnchor); setMatchState(s.matchState);
      setSkip(s.skip); setHasMore(s.hasMore); setLoading(false);
      setTimeout(() => window.scrollTo(0, s.scrollY || 0), 0);
      return true;
    } catch { return false; }
  };

  // Single shared jobs request runner: aborts any obsolete in-flight request, ignores
  // stale responses (sequence guard), and dedupes an identical concurrent request.
  const fetchPage = async (url, { append, viewMeta }) => {
    if (inFlightUrlRef.current === url) return; // dedupe identical concurrent request
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const seq = ++reqSeqRef.current;
    inFlightUrlRef.current = url;
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const response = await axios.get(url, { withCredentials: true, signal: controller.signal });
      if (seq !== reqSeqRef.current) return; // a newer request superseded this one
      const data = response.data;
      const t = readTotal(response, append ? total : data.length);
      setMatchState(readMatchState(response));
      if (ENGINE === 'v2') setLocAvail(readLoc(response));
      if (append) {
        let newLen = 0;
        setJobs(prev => {
          const seen = new Set(prev.map(j => j.job_id));
          const merged = [...prev, ...data.filter(j => !seen.has(j.job_id))];
          newLen = merged.length;
          return merged;
        });
        setSkip(prev => prev + data.length);
        setTotal(t);
        setHasMore(newLen < t);
      } else {
        setJobs(data);
        setSkip(data.length);
        setTotal(t);
        setHasMore(data.length < t);
        // Fire-and-forget: one opportunities_viewed per completed first-page
        // presentation (guarded so rerenders / silent revalidation / identical
        // repeat requests never double-count). Snapshot restore never calls this.
        if (url !== lastViewedUrlRef.current) {
          lastViewedUrlRef.current = url;
          try { sessionStorage.setItem(LAST_VIEW_KEY, JSON.stringify({ url, ts: Date.now() })); } catch {}
          emitEvent('opportunities_viewed', { result_count: t, ...(viewMeta || {}) });
        }
      }
    } catch (e) {
      if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED' || axios.isCancel?.(e)) return;
      toast.error(append ? 'Failed to load more' : 'Search failed');
    } finally {
      if (inFlightUrlRef.current === url) inFlightUrlRef.current = null;
      if (seq === reqSeqRef.current) { if (append) setLoadingMore(false); else setLoading(false); }
    }
  };

  useEffect(() => {
    const init = async () => {
      // Rehydrate the last opportunities_viewed URL (2-min window) so returning to
      // Opportunities never re-emits a view for the same query, even if the full-view
      // snapshot restore falls through to a fresh fetch of the identical URL.
      try {
        const rawLv = sessionStorage.getItem(LAST_VIEW_KEY);
        if (rawLv) { const lv = JSON.parse(rawLv); if (lv && Date.now() - (lv.ts || 0) <= 120000) lastViewedUrlRef.current = lv.url; }
      } catch {}
      // Return-to-Opportunities: restore the preserved view (user-scoped) after we know
      // who is logged in, so a not-ready / different seeker never inherits stale scores.
      let restored = false;
      try {
        const userRes = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
        setUser(userRes.data);
        restored = restoreSnapshot(userRes.data?.id);
        // Resolve the minimum initial state (profile context + location anchor + coords)
        // BEFORE issuing the one canonical jobs request.
        let anchor = 'anywhere', hc = null, bc = null;
        let ready = false;
        if (userRes.data?.role === 'job_seeker') {
          try {
            const resumeRes = await axios.get(`${BACKEND_URL}/api/resume`, { withCredentials: true });
            ready = !!resumeRes.data.is_complete;
            setResumeStatus({ is_complete: resumeRes.data.is_complete, visible: resumeRes.data.visible });
            setResume(resumeRes.data);
            const nhl = resumeRes.data.normalized_home_location;
            if (nhl?.city && nhl?.state) setHomeLocation(`${nhl.city}, ${nhl.state}`);
            hc = (nhl && isValidCoord(nhl.latitude, nhl.longitude)) ? { lat: nhl.latitude, lng: nhl.longitude } : null;
            for (const role of (resumeRes.data.military_roles || [])) {
              const snap = role?.base_snapshot;
              if (snap && isValidCoord(snap.latitude, snap.longitude)) { bc = { lat: snap.latitude, lng: snap.longitude }; break; }
            }
            setHomeCoords(hc); setBaseCoords(bc);
            anchor = bc ? 'base' : (hc ? 'home' : 'anywhere');
            // A complete resume with NO usable Base/Home coordinates → fall back to
            // Anywhere, but surface a clear notice (never behave as Anywhere silently).
            setLocationFallback(!!(resumeRes.data.is_complete && !bc && !hc));
            if (!restored) setLocationAnchor(anchor); // keep restored anchor when returning
          } catch { setResumeStatus({ is_complete: false, visible: false }); }
        }
        // Favorites load is independent of jobs — never blocks/precedes the jobs request.
        axios.get(`${BACKEND_URL}/api/jobs/favorites/list`, { withCredentials: true })
          .then(r => setFavorites(new Set(r.data.map(j => j.job_id)))).catch(() => {});
        // Initialize the Opportunity Types control from the persisted account preference:
        //   skillbridge_match_enabled=true  → Jobs + SkillBridge (opportunity_type '')
        //   false/unset                     → Jobs only (opportunity_type 'employment')
        // (Skip when restoring a preserved view so the returning filter selection wins.)
        let initOpp = filters.opportunity_type;
        if (!restored && userRes.data?.role === 'job_seeker') {
          initOpp = userRes.data?.skillbridge_match_enabled ? '' : 'employment';
          setFilters(prev => ({ ...prev, opportunity_type: initOpp }));
        }
        // THE single canonical initial jobs request — only when not restoring a fresh view.
        // Not match-ready seekers must not default to Best Match; use Newest instead.
        if (!restored) {
          const initSort = (sortBy === 'best_match' && userRes.data?.role === 'job_seeker' && !ready) ? 'newest' : sortBy;
          if (initSort !== sortBy) setSortBy(initSort);
          await fetchFirstPage({ anchor, coords: { home: hc, base: bc }, filters: { radius_miles: '50', opportunity_type: initOpp }, sort: initSort });
        }
        // AUTO-START (Opportunities readiness): entering /jobs ALWAYS evaluates canonical V2
        // readiness for a match-ready seeker and engages the EXISTING ensure/poll lifecycle when
        // the canonical seeker version lacks current scores — with NO filter / sort / distance /
        // SkillBridge interaction required. If current scores already exist (status 'ready') it
        // does nothing (never rebuilds). Universe (jobs vs jobs_sb) is resolved server-side.
        if (ENGINE === 'v2' && userRes.data?.role === 'job_seeker' && ready) {
          try {
            const sres = await axios.get(`${BACKEND_URL}${MATCH_STATUS_PATH}`, { withCredentials: true });
            const st = sres.data?.status;
            try { diagLog('opportunities_readiness_autostart', { status: st,
              ready: sres.data?.ready, missing: sres.data?.missing,
              seeker_version: sres.data?.seeker_version,
              component_instance_id: diagInstanceId }); } catch (e) {}
            if (st === 'preparing') setMatchState('preparing');  // engages ensure/poll; no rebuild if ready
          } catch (e) {}
        }
      } catch (error) {
        console.error('Failed to load Opportunities:', error);
        setLoading(false);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchFirstPage = async (overrides = {}) => {
    const f = { ...filters, ...(overrides.filters || {}) };
    const anchor = overrides.anchor !== undefined ? overrides.anchor : locationAnchor;
    const coords = overrides.coords || { home: homeCoords, base: baseCoords, other: otherCoords };
    const sortVal = overrides.sort !== undefined ? overrides.sort : sortBy;
    const url = `${BACKEND_URL}${JOBS_PATH}?${buildParams(f, 0, anchor, coords, sortVal).toString()}`;
    await fetchPage(url, { append: false, viewMeta: { opportunity_type: f.opportunity_type || 'all', sort_by: sortVal } });
  };

  const searchJobs = () => {
    // Deliberate keyword Search — never send the raw keyword text.
    emitEvent('opportunity_search', { keyword_present: !!filters.keyword, opportunity_type: filters.opportunity_type || 'all' });
    fetchFirstPage();
  };

  // While Best Match is materializing, poll the lightweight readiness endpoint (~2.5s) and
  // auto-refresh into true Best Match once ready. Stops on ready/failed/incomplete and is
  // bounded so it never polls forever; browsing stays available throughout.
  useEffect(() => {
    if (matchState !== 'preparing') { setMatchStalled(false); return; }
    let cancelled = false;
    let polls = 0;
    const MAX_POLLS = 120; // ~5 min safety cap
    progressRef.current = { scored: -1, ts: Date.now() };
    readyEdgeRef.current = false;
    setMatchStalled(false);
    const STALL_MS = 40000; // ~40s with no progress → restrained "taking longer" message
    // V2: GET never schedules scoring — kick materialization once when we enter preparing.
    if (ENGINE === 'v2') {
      try { diagLog('match_ensure_decision', { match_state: matchState, ensure_called: true,
        reason: 'entered_preparing', engine: ENGINE, component_instance_id: diagInstanceId }); } catch (e) {}
      axios.post(`${BACKEND_URL}/api/match-v2/ensure`, {}, { withCredentials: true }).catch(() => {});
    }
    const id = setInterval(async () => {
      polls += 1;
      if (polls > MAX_POLLS) { clearInterval(id); return; }
      try {
        const r = await axios.get(`${BACKEND_URL}${MATCH_STATUS_PATH}`, { withCredentials: true });
        if (cancelled) return;
        const st = r.data?.status;
        const scored = typeof r.data?.scored === 'number' ? r.data.scored
          : (typeof r.data?.task?.scored === 'number' ? r.data.task.scored : null);
        if (st === 'ready') {
          // Fetch Best Match EXACTLY ONCE per ready-edge. Do NOT clearInterval here: if that
          // fetch discovers a stale/missing score it re-opens materialization and GET returns
          // 'preparing' again (a no-op setState that would NOT re-run this effect) — keeping the
          // timer alive lets us keep polling until a TRUE ready. Timer stops via effect cleanup
          // once matchState actually becomes 'ready'.
          if (sortByRef.current === 'best_match') {
            if (!readyEdgeRef.current) {
              readyEdgeRef.current = true;
              toast.success('Your matches are ready');
              fetchFirstPage();
            }
          } else {
            clearInterval(id);
            setMatchState('ready');
          }
          return;
        }
        // Non-ready status → allow the next true ready to refetch once (re-arm after reopen).
        readyEdgeRef.current = false;
        // 'unavailable' = Match V2 materialization is disabled (kill switch) → stop polling and
        // fall back to ordinary browse; do NOT keep re-firing ensure.
        if (st === 'failed' || st === 'incomplete' || st === 'unavailable') {
          clearInterval(id);
          setMatchState(st);
          return;
        }
        // Match no longer applies (e.g. resume lacks military roles) → never keep the
        // "Personalizing matches" UI. Clear preparing state and stop polling.
        if (st === 'not_applicable') {
          clearInterval(id);
          setMatchState('not_applicable');
          return;
        }
        // SELF-HEAL (V2): status reports missing coverage but NO task exists (task.status is
        // null) → the one-shot ensure at effect start never landed (e.g. a transient 520/timeout
        // during CPU saturation, silently swallowed). Re-fire ensure. It is idempotent (durable
        // DB lease + single-winner), so this recovers the stuck "missing=N, task=null" state
        // without duplicate materializations; it stops firing the moment a task exists.
        if (ENGINE === 'v2' && st !== 'ready' && !r.data?.task?.status) {
          axios.post(`${BACKEND_URL}/api/match-v2/ensure`, {}, { withCredentials: true }).catch(() => {});
        }
        // status still preparing — track whether `scored` is advancing (frontend-only).
        if (scored !== null && scored > progressRef.current.scored) {
          progressRef.current = { scored, ts: Date.now() };
          setMatchStalled(false); // progressing → not a stall
        } else if (Date.now() - progressRef.current.ts > STALL_MS) {
          setMatchStalled(true);  // no forward progress for a while (no extra requests)
        }
      } catch {}
    }, 2500); // low-frequency cadence; never sped up on stall
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchState]);

  // Instant-apply single-select filters — update results immediately (no Search press).
  const applyInstant = (patch) => {
    emitEvent('opportunity_filter_changed', { filters_changed: Object.keys(patch) });
    setFilters(prev => ({ ...prev, ...patch }));
    fetchFirstPage({ filters: patch });
  };

  // Fire-and-forget UI analytics. NEVER blocks the UI and never touches the jobs
  // fetch pipeline; failures are ignored. No raw keyword/coordinates are ever sent.
  const emitEvent = (event_name, metadata = {}, entity_type, entity_id) => {
    try {
      axios.post(`${BACKEND_URL}/api/activity/event`, { event_name, metadata, entity_type, entity_id },
        { withCredentials: true }).catch(() => {});
    } catch { /* ignore */ }
  };

  // Sort applies immediately; pass the new value to avoid stale-closure.
  const changeSort = (v) => {
    // Not match-ready → Best Match is unavailable; fall back to a neutral Newest sort.
    if (v === 'best_match' && !matchReady) v = 'newest';
    emitEvent('opportunity_sort_changed', { from_sort: sortBy, to_sort: v });
    setSortBy(v);
    fetchFirstPage({ sort: v });
  };

  // Location Anchor switch — resets pagination and re-runs the search cleanly so no
  // stale hidden origin/location params leak into the next request.
  const changeAnchor = (anchor) => {
    setLocationAnchor(anchor);
    if (anchor === 'anywhere' || anchor === 'base' || anchor === 'home') {
      emitEvent('opportunity_filter_changed', { filters_changed: ['location_anchor'] });
      fetchFirstPage({ anchor });
    }
    // 'other' → reveal text input; user types then hits Search.
  };

  const changeRadius = (r) => {
    setFilters(prev => ({ ...prev, radius_miles: r }));
    if (locationAnchor !== 'anywhere' && !(locationAnchor === 'other' && !filters.location)) {
      emitEvent('opportunity_filter_changed', { filters_changed: ['radius'] });
      fetchFirstPage({ filters: { radius_miles: r } });
    }
    // Radius is an immediately-applied choice → auto-close the selector (requirement C).
    setLocOpen(false); setLocOpenM(false);
  };

  const loadMore = async () => {
    const url = `${BACKEND_URL}${JOBS_PATH}?${buildParams(filters, skip, locationAnchor, { home: homeCoords, base: baseCoords, other: otherCoords }, sortBy).toString()}`;
    await fetchPage(url, { append: true });
  };

  const toggleFavorite = async (jobId) => {
    const wasFav = favorites.has(jobId);
    try {
      if (wasFav) {
        await axios.delete(`${BACKEND_URL}/api/jobs/${jobId}/favorite`, { withCredentials: true });
        setFavorites(prev => { const s = new Set(prev); s.delete(jobId); return s; });
        toast.success('Removed from favorites');
      } else {
        await axios.post(`${BACKEND_URL}/api/jobs/${jobId}/favorite`, {}, { withCredentials: true });
        setFavorites(prev => new Set(prev).add(jobId));
        toast.success('Added to favorites');
      }
      // In Saved mode a change must reflect live: drop an unfavorited job from the list instantly
      // and re-query the map so its (saved-only) cluster counts update — no page refresh.
      if (filters.saved_only) {
        if (wasFav) {
          setJobs(prev => prev.filter(j => j.job_id !== jobId));
          setTotal(t => Math.max(0, t - 1));
        }
        setSavedNonce(n => n + 1);
      }
    } catch { toast.error('Failed to update favorite'); }
  };

  // Open the Zillow-style preview for a job selected on the map (single pin or stack row).
  // Resolves richer context from the already-loaded list (match score, explanation, distance)
  // without any extra map/scoring request; falls back to the map pin's stored score hint.
  const openPreview = (jobId, hint = {}) => {
    const lj = jobs.find(j => j.job_id === jobId) || null;
    const ms = matchReady && lj?.match_score != null ? lj.match_score : (matchReady ? (hint?.match_score ?? null) : null);
    setPreview({ jobId, listJob: lj, matchScore: ms });
  };

  // ── Opportunity Types (unified source control) ──────────────────────────────
  // Two chips [Jobs] [SkillBridge] map to the existing display filter + account preference:
  //   Jobs + SkillBridge  → opportunity_type '' (All),          skillbridge_match_enabled = true
  //   Jobs only           → opportunity_type 'employment',       skillbridge_match_enabled = false
  //   SkillBridge only     → opportunity_type 'skillbridge',      skillbridge_match_enabled = true
  // Selecting SkillBridge simply invokes the EXISTING preference/materialization behavior; the
  // backend scope/readiness/score-reuse logic is unchanged.
  const jobsSelected = filters.opportunity_type === '' || filters.opportunity_type === 'employment';
  const sbSelected = filters.opportunity_type === '' || filters.opportunity_type === 'skillbridge';
  const [sbToggleBusy, setSbToggleBusy] = useState(false);
  // SkillBridge eligibility notice is dismissible for the browser session (sessionStorage → returns
  // in a new session). Once dismissed it stays hidden even if SkillBridge is toggled off then on.
  const [sbNoteDismissed, setSbNoteDismissed] = useState(
    () => typeof window !== 'undefined' && sessionStorage.getItem('sb_elig_note_dismissed') === '1');
  const dismissSbNote = () => {
    try { sessionStorage.setItem('sb_elig_note_dismissed', '1'); } catch { /* ignore */ }
    setSbNoteDismissed(true);
  };

  const toggleSource = async (which) => {
    if (sbToggleBusy) return;
    let jobs = jobsSelected, sb = sbSelected;
    if (which === 'jobs') jobs = !jobs; else sb = !sb;
    if (!jobs && !sb) return; // never allow both unselected
    const opp = (jobs && sb) ? '' : (jobs ? 'employment' : 'skillbridge');
    const wantPref = sb;
    const curPref = !!user?.skillbridge_match_enabled;
    // Persist the account preference ONLY when it changes, and BEFORE re-fetching so the server
    // reads the correct Match scope for this request.
    if (wantPref !== curPref) {
      setSbToggleBusy(true);
      try {
        await axios.put(`${BACKEND_URL}/api/auth/skillbridge-match-preference`,
          { enabled: wantPref }, { withCredentials: true });
      } catch {
        toast.error('Could not update SkillBridge matching. Please try again.');
        setSbToggleBusy(false);
        return;
      }
      setSbToggleBusy(false);
      setUser(u => ({ ...(u || {}), skillbridge_match_enabled: wantPref }));
      emitEvent('skillbridge_match_preference_changed', { enabled: wantPref });
      if (wantPref) toast.message('Adding SkillBridge opportunities to your matches…');
    }
    const patch = { opportunity_type: opp };
    if (opp !== 'employment') { patch.job_type = ''; patch.salary_min = ''; }
    setFilters(prev => ({ ...prev, ...patch }));
    emitEvent('opportunity_filter_changed', { filters_changed: ['opportunity_types'], opportunity_type: opp || 'all' });
    fetchFirstPage({ filters: patch });
  };

  const handleKeyPress = (e) => { if (e.key === 'Enter') searchJobs(); };

  // Map recenter target: derived ONLY from the explicit location/radius controls. Changing the
  // anchor or radius resets the map to that geography; the map is otherwise free to explore. For
  // 'other' (free-text, unresolved client-side) we pass null so the map is NOT snapped anywhere.
  const RADIUS_ZOOM = { '10': 11, '25': 10, '50': 9, '100': 8, any: 5 };
  const focusCoords = locationAnchor === 'base' ? baseCoords
    : locationAnchor === 'home' ? homeCoords
    : locationAnchor === 'other' ? otherCoords : null;
  // 'other' with no resolved coords yet → null (map stays until geocode returns, then recenters once).
  const mapFocus = (locationAnchor === 'other' && !otherCoords) ? null : {
    key: `${locationAnchor}|${filters.radius_miles}|${focusCoords ? `${focusCoords.lat},${focusCoords.lng}` : ''}`,
    lng: focusCoords ? focusCoords.lng : null,
    lat: focusCoords ? focusCoords.lat : null,
    zoom: focusCoords ? (RADIUS_ZOOM[filters.radius_miles] ?? 9) : undefined,
  };

  // ── Combined LOCATION + RADIUS control ───────────────────────────────────────
  const RADII = [['10', '10 mi'], ['25', '25 mi'], ['50', '50 mi'], ['100', '100 mi'], ['any', 'Unlimited']];
  const radiusLabel = (r) => (r === 'any' ? 'Unlimited' : `${r} mi`);
  const isJobSeeker = user?.role === 'job_seeker';
  // Readiness states — a location is usable for distance only with meaningful coordinates.
  const homeStatus = homeCoords ? 'ready' : (homeLocation ? 'fix' : 'set');
  const baseAddr = (resume?.military_roles || []).map(r => r?.location).find(Boolean) || null;
  const baseStatus = baseCoords ? 'ready' : (baseAddr ? 'fix' : 'set');
  const locLabel = locationAnchor === 'anywhere' ? 'Anywhere'
    : locationAnchor === 'home' ? `Near Home · ${radiusLabel(filters.radius_miles)}`
    : locationAnchor === 'base' ? `Near Current Base · ${radiusLabel(filters.radius_miles)}`
    : (filters.location ? `Near ${filters.location} · ${radiusLabel(filters.radius_miles)}` : 'Set search area');
  const moreActiveCount = (filters.job_type ? 1 : 0) + (filters.salary_min ? 1 : 0);
  const mobileActiveCount = moreActiveCount + (filters.remote ? 1 : 0) + (filters.saved_only ? 1 : 0);

  const applyAnotherLocation = async () => {
    const loc = otherDraft.trim();
    if (!loc) return;
    // Geocode FIRST (single resolved source, reused by BOTH list and map). Only on success do we
    // switch the anchor and issue geographic queries — never a misleading "Near X" with a text match.
    let coords = null;
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/map/geocode`, { params: { q: loc }, withCredentials: true });
      if (data?.resolved) coords = { lat: data.lat, lng: data.lng };
    } catch { /* handled below */ }
    if (!coords) {
      toast.error("We couldn't find that location. Try a city, state, or ZIP.");
      return; // preserve the previous valid search state; do not move the map or query geographically
    }
    setOtherCoords(coords);
    setLocationAnchor('other');
    setFilters(prev => ({ ...prev, location: loc }));
    emitEvent('opportunity_filter_changed', { filters_changed: ['location_anchor'] });
    // One list refresh using the SAME resolved coords + radius as the map (geographic, not text).
    fetchFirstPage({ anchor: 'other', filters: { location: loc },
      coords: { home: homeCoords, base: baseCoords, other: coords } });
    setLocOpen(false); setLocOpenM(false);
    // Map recenter is driven by otherCoords via mapFocus (one bounded map request).
  };
  const goFixLocation = (key) => {
    setLocOpen(false); setLocOpenM(false);
    // Route to the EXACT location editor — NOT the Review step / resume regeneration.
    // Home lives in Personal Info (step 1); Base/duty station lives in Military Career (step 2).
    const step = key === 'home' ? 1 : 2;
    navigate(`/resume-builder?step=${step}`);
  };

  // Anchor row for Home / Current Base inside the location popover — shows ready state or a
  // clear, ACTIONABLE recovery CTA (never a dead "(unavailable)" option). Repair routes to the
  // canonical profile/location editor (resume builder); see report notes.
  const anchorRow = (key, label, coords, status, addr) => {
    const active = locationAnchor === key;
    return (
      <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg hover:bg-slate-50" data-testid={`loc-anchor-${key}`}>
        <button type="button" disabled={status !== 'ready'}
          onClick={() => { changeAnchor(key); setLocOpen(false); setLocOpenM(false); }}
          className={`flex-1 text-left min-w-0 ${status !== 'ready' ? 'opacity-70 cursor-default' : ''}`}>
          <div className={`text-sm font-medium ${active ? 'text-brand' : 'text-slate-800'} flex items-center gap-1.5`}>
            {active && <Check className="w-3.5 h-3.5" strokeWidth={2.5} />}{label}
          </div>
          <div className="text-xs mt-0.5 truncate">
            {status === 'ready' && <span className="text-emerald-600">{addr || 'Ready'}</span>}
            {status === 'fix' && <span className="text-amber-600">{addr ? `${addr} — location not verified` : 'Location not verified'}</span>}
            {status === 'set' && <span className="text-slate-400">No location saved</span>}
          </div>
        </button>
        {status !== 'ready' && (
          <button type="button" onClick={() => goFixLocation(key)} data-testid={`loc-${key}-fix-btn`}
            className="shrink-0 text-xs font-semibold text-brand hover:underline whitespace-nowrap">
            {status === 'set' ? 'Set location' : 'Fix'}
          </button>
        )}
      </div>
    );
  };

  const locationMenuBody = () => (
    <div className="p-1.5" data-testid="location-menu">
      <div className="px-2 pt-1.5 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Search around</div>
      {isJobSeeker && anchorRow('home', 'Home', homeCoords, homeStatus, homeLocation)}
      {isJobSeeker && anchorRow('base', 'Current Base', baseCoords, baseStatus, baseAddr)}
      {/* Another location (ad hoc, session only — never overwrites Home/Base) */}
      <div className="px-3 py-2 rounded-lg hover:bg-slate-50" data-testid="loc-anchor-other">
        <div className="text-sm font-medium text-slate-800 flex items-center gap-1.5">
          {locationAnchor === 'other' && <Check className="w-3.5 h-3.5 text-brand" strokeWidth={2.5} />}Another location
        </div>
        <div className="mt-1.5 flex gap-1.5">
          <Input data-testid="location-search-input" value={otherDraft}
            onChange={(e) => setOtherDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') applyAnotherLocation(); }}
            placeholder="City, State, or ZIP" className="h-9 text-sm" />
          <Button size="sm" onClick={applyAnotherLocation} data-testid="apply-location-btn"
            className="h-9 bg-brand hover:bg-brand-hover text-white shrink-0">Go</Button>
        </div>
      </div>
      <button type="button" onClick={() => { changeAnchor('anywhere'); setLocOpen(false); setLocOpenM(false); }}
        data-testid="loc-anchor-anywhere"
        className={`w-full text-left px-3 py-2 rounded-lg hover:bg-slate-50 text-sm font-medium flex items-center gap-1.5 ${locationAnchor === 'anywhere' ? 'text-brand' : 'text-slate-800'}`}>
        {locationAnchor === 'anywhere' && <Check className="w-3.5 h-3.5" strokeWidth={2.5} />}Anywhere
      </button>
      {/* Distance — hidden for Anywhere (radius irrelevant) */}
      {locationAnchor !== 'anywhere' && (
        <>
          <div className="mt-1 border-t border-slate-100" />
          <div className="px-2 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Distance</div>
          <div className="flex flex-wrap gap-1.5 px-2 pb-1.5" data-testid="radius-options">
            {RADII.map(([val, lbl]) => (
              <button key={val} type="button" data-testid={`radius-opt-${val}`}
                onClick={() => changeRadius(val)}
                className={`h-8 px-2.5 rounded-md text-xs font-medium border transition-colors ${filters.radius_miles === val ? 'border-brand bg-brand-light text-brand' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}>
                {lbl}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );

  const jobsSbSegmented = () => (
    <div className="inline-flex items-center h-11 rounded-lg border border-slate-200 bg-slate-50 px-0.5 shrink-0"
         data-testid="opportunity-types-control" role="group" aria-label="Opportunity Types">
      <button type="button" data-testid="source-chip-jobs" aria-pressed={jobsSelected}
        onClick={() => toggleSource('jobs')} disabled={sbToggleBusy}
        className={`h-9 px-3 rounded-md text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${jobsSelected ? 'bg-white text-brand shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
        {jobsSelected && <Check className="w-3.5 h-3.5" strokeWidth={2.5} />}Jobs
      </button>
      <button type="button" data-testid="source-chip-skillbridge" aria-pressed={sbSelected}
        onClick={() => toggleSource('skillbridge')} disabled={sbToggleBusy}
        className={`h-9 px-3 rounded-md text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${sbSelected ? 'bg-white text-brand shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
        {sbSelected && <Check className="w-3.5 h-3.5" strokeWidth={2.5} />}SkillBridge
      </button>
    </div>
  );

  const workArrangementSelect = (cls = '') => (
    <select data-testid="remote-filter" value={filters.remote} onChange={(e) => applyInstant({ remote: e.target.value })}
      className={`h-11 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand/30 ${cls}`}>
      <option value="">Work Arrangement</option>
      <option value="true">Remote Only</option>
      <option value="false">On-site Only</option>
    </select>
  );

  const savedChip = (cls = '') => (isJobSeeker && (
    <button type="button" data-testid="saved-filter-chip" aria-pressed={filters.saved_only}
      onClick={() => applyInstant({ saved_only: !filters.saved_only })}
      className={`h-11 rounded-lg border px-3.5 text-sm font-medium inline-flex items-center gap-1.5 transition-colors shrink-0 ${filters.saved_only ? 'border-red-200 bg-red-50 text-red-600' : 'border-slate-200 bg-slate-50 text-slate-600 hover:text-slate-800'} ${cls}`}>
      <Heart className={`w-4 h-4 ${filters.saved_only ? 'fill-red-500 text-red-500' : ''}`} />Saved
    </button>
  ));

  const jobTypeMinSalary = () => (
    <>
      <div>
        <Label className="text-slate-700 text-sm">Job type</Label>
        <select data-testid="job-type-filter" value={filters.job_type} onChange={(e) => applyInstant({ job_type: e.target.value })}
          className="mt-1.5 h-10 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand/30">
          <option value="">Any job type</option>
          <option value="full_time">Full Time</option>
          <option value="part_time">Part Time</option>
          <option value="contract">Contract</option>
          <option value="temporary">Temporary</option>
        </select>
      </div>
      <div>
        <Label className="text-slate-700 text-sm">Minimum salary</Label>
        <select data-testid="salary-min-select" value={filters.salary_min} onChange={(e) => applyInstant({ salary_min: e.target.value })}
          className="mt-1.5 h-10 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand/30">
          <option value="">Any salary</option>
          <option value="40000">$40k+</option>
          <option value="60000">$60k+</option>
          <option value="80000">$80k+</option>
          <option value="100000">$100k+</option>
          <option value="120000">$120k+</option>
          <option value="150000">$150k+</option>
        </select>
      </div>
    </>
  );

  return (
    <Layout user={user}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <PageHeader
          icon={Briefcase}
          title="Opportunities"
          subtitle={(isScored && matchState !== 'preparing') ? 'Roles in your region \u2014 ranked using your MOS, skills, and location' : 'Roles in your region \u2014 and a few beyond.'}
        />

        {/* Resume Banners */}
        {user?.role === 'job_seeker' && resumeStatus && !resumeStatus.is_complete && (
          <div className="mb-4 flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3" data-testid="resume-incomplete-banner">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-800">Complete your resume for personalized job matches</p>
              <p className="text-xs text-amber-600 mt-0.5">We'll rank jobs based on your MOS, skills, and location.</p>
            </div>
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white rounded-lg" onClick={() => navigate('/resume-builder')} data-testid="complete-resume-btn">
              Complete Resume
            </Button>
          </div>
        )}
        {user?.role === 'job_seeker' && resumeStatus && resumeStatus.is_complete && !resumeStatus.visible && (
          <div className="mb-4 flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3" data-testid="resume-visibility-banner">
            <Eye className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-blue-800">Make your profile visible to employers</p>
              <p className="text-xs text-blue-600 mt-0.5">Employers can't find you in search until your profile is visible.</p>
            </div>
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg" onClick={async () => {
              try {
                const res = await axios.put(`${BACKEND_URL}/api/resume`, { visible: true }, { withCredentials: true });
                setResumeStatus({ is_complete: res.data.is_complete, visible: res.data.visible });
                toast.success('Profile is now visible to employers');
              } catch { toast.error('Failed to update visibility'); }
            }} data-testid="make-visible-btn">
              Make Visible
            </Button>
          </div>
        )}

        {/* Location data unavailable → transparent Anywhere fallback notice */}
        {user?.role === 'job_seeker' && locationFallback && locationAnchor === 'anywhere' && (
          <div className="mb-4 flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3" data-testid="location-fallback-banner">
            <MapPin className="w-5 h-5 text-slate-400 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-slate-700">Showing opportunities everywhere</p>
              <p className="text-xs text-slate-500 mt-0.5">We couldn't determine your base or home location, so distance-based sorting is unavailable. Add a location to your profile to see roles near you.</p>
            </div>
            <Button size="sm" variant="outline" className="rounded-lg" onClick={() => navigate('/resume-builder')} data-testid="add-location-btn">
              Update Profile
            </Button>
          </div>
        )}

        {/* Search & Filters */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 mb-6" data-testid="job-search-filters">
          {/* ROW 1 — SEARCH: keyword text query (Enter or Search button only; never on keystroke) */}
          <div className="flex flex-col sm:flex-row gap-2.5 mb-2.5">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="keyword-search-input"
                placeholder="Job title, skills, keywords..."
                value={filters.keyword}
                onChange={(e) => setFilters({...filters, keyword: e.target.value})}
                onKeyDown={handleKeyPress}
                className="h-11 pl-10"
              />
            </div>
            <Button
              data-testid="search-jobs-btn"
              onClick={searchJobs}
              className="h-11 px-6 bg-brand hover:bg-brand-hover text-white rounded-lg shrink-0 w-full sm:w-auto"
            >
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
          </div>
          {/* ROW 2 — DESKTOP: one compact filter toolbar (h-11 controls) */}
          <div className="hidden sm:flex items-center gap-2.5">
            <Popover open={locOpen} onOpenChange={setLocOpen}>
              <PopoverTrigger asChild>
                <button type="button" data-testid="location-control"
                  className="h-11 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 pl-3 pr-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors max-w-[260px]">
                  <MapPin className="w-4 h-4 text-slate-400 shrink-0" />
                  <span className="truncate">{locLabel}</span>
                  <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-[300px] p-0" data-testid="location-popover">
                {locationMenuBody()}
              </PopoverContent>
            </Popover>
            {jobsSbSegmented()}
            {workArrangementSelect()}
            {savedChip()}
            <Popover open={genMoreOpen} onOpenChange={setGenMoreOpen}>
              <PopoverTrigger asChild>
                <button type="button" data-testid="more-filters-toggle"
                  className={`h-11 inline-flex items-center gap-2 rounded-lg border px-3.5 text-sm font-medium transition-colors shrink-0 ${moreActiveCount ? 'border-brand/40 bg-brand-light text-brand' : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100'}`}>
                  <SlidersHorizontal className="w-4 h-4" />
                  More Filters{moreActiveCount ? ` · ${moreActiveCount}` : ''}
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-[280px] p-4 space-y-3" data-testid="more-filters-popover">
                {jobTypeMinSalary()}
              </PopoverContent>
            </Popover>
            {filters.opportunity_type === 'skillbridge' && (
              <Button data-testid="sb-more-filters-btn" variant="outline" onClick={openMoreFilters}
                className="h-11 rounded-lg border-slate-200 bg-slate-50 px-3 text-sm text-slate-700 shrink-0">
                <SlidersHorizontal className="w-4 h-4 mr-2" />
                SkillBridge Filters{sbActiveCount > 0 ? ` · ${sbActiveCount}` : ''}
              </Button>
            )}
          </div>

          {/* ROW 2 — MOBILE: deliberate compact pattern [ Location ] [ Filters ] */}
          <div className="flex sm:hidden items-center gap-2">
            <Popover open={locOpenM} onOpenChange={setLocOpenM}>
              <PopoverTrigger asChild>
                <button type="button" data-testid="location-control-mobile"
                  className="h-11 flex-1 min-w-0 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 pl-3 pr-2 text-sm font-medium text-slate-700">
                  <MapPin className="w-4 h-4 text-slate-400 shrink-0" />
                  <span className="truncate flex-1 text-left">{locLabel}</span>
                  <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-[calc(100vw-2rem)] max-w-[340px] p-0" data-testid="location-popover-mobile">
                {locationMenuBody()}
              </PopoverContent>
            </Popover>
            <Popover open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
              <PopoverTrigger asChild>
                <button type="button" data-testid="mobile-filters-toggle"
                  className={`h-11 inline-flex items-center gap-1.5 rounded-lg border px-3.5 text-sm font-medium shrink-0 ${mobileActiveCount ? 'border-brand/40 bg-brand-light text-brand' : 'border-slate-200 bg-slate-50 text-slate-700'}`}>
                  <SlidersHorizontal className="w-4 h-4" />
                  Filters{mobileActiveCount ? ` · ${mobileActiveCount}` : ''}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-[calc(100vw-2rem)] max-w-[340px] p-4 space-y-4" data-testid="mobile-filters-panel">
                <div>
                  <Label className="text-slate-700 text-sm mb-1.5 block">Opportunity type</Label>
                  {jobsSbSegmented()}
                </div>
                <div className="flex items-center gap-2">
                  {workArrangementSelect('flex-1')}
                  {savedChip()}
                </div>
                {jobTypeMinSalary()}
                {filters.opportunity_type === 'skillbridge' && (
                  <Button data-testid="sb-more-filters-btn-mobile" variant="outline"
                    onClick={() => { setMobileFiltersOpen(false); openMoreFilters(); }}
                    className="w-full h-10 rounded-lg border-slate-200 bg-slate-50 text-sm text-slate-700">
                    SkillBridge Filters{sbActiveCount > 0 ? ` · ${sbActiveCount}` : ''}
                  </Button>
                )}
              </PopoverContent>
            </Popover>
          </div>
          {/* SkillBridge eligibility caveat — compact amber notice attached to the SkillBridge
              controls at the bottom of the filter card; shown ONLY when SkillBridge is selected.
              Informational only; no enforcement. */}
          {sbSelected && !sbNoteDismissed && (
            <div
              data-testid="skillbridge-eligibility-note"
              className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] leading-snug text-amber-800"
            >
              <Info className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" strokeWidth={2} />
              <span>SkillBridge is for eligible service members approaching separation and requires
                command/service approval. Confirm eligibility with your command or transition office.</span>
              <button type="button" onClick={dismissSbNote} aria-label="Dismiss SkillBridge eligibility notice"
                data-testid="skillbridge-eligibility-dismiss"
                className="ml-auto -mr-1 -mt-0.5 p-0.5 text-amber-500 hover:text-amber-700 transition-colors flex-shrink-0">
                <X className="w-4 h-4" strokeWidth={2} />
              </button>
            </div>
          )}
        </div>

        {/* SkillBridge More Filters panel */}
        <Dialog open={moreOpen} onOpenChange={setMoreOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="more-filters-panel">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-slate-900">More filters</DialogTitle>
              <DialogDescription className="text-slate-500">Refine SkillBridge opportunities using official program data.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-1">
              <SbSelect label="Career field" testid="sb-career-field" value={sbDraft.career_field}
                onChange={(v) => setSbDraft(d => ({ ...d, career_field: v }))} options={sbOptions?.career_fields} />
              <SbSelect label="Program type" testid="sb-program-type" value={sbDraft.program_type}
                onChange={(v) => setSbDraft(d => ({ ...d, program_type: v }))} options={sbOptions?.program_types} />
              <div className="grid grid-cols-2 gap-3">
                <SbSelect label="Duration" testid="sb-duration" value={sbDraft.duration}
                  onChange={(v) => setSbDraft(d => ({ ...d, duration: v }))} options={sbOptions?.durations} />
                <SbSelect label="State / region" testid="sb-state" value={sbDraft.state}
                  onChange={(v) => setSbDraft(d => ({ ...d, state: v }))} options={sbOptions?.states} />
              </div>
              <div>
                <Label className="text-slate-700 text-sm">Eligible branches</Label>
                <div className="flex flex-wrap gap-2 mt-1.5" data-testid="sb-branches">
                  {(sbOptions?.branches || []).map(b => (
                    <button key={b} type="button" onClick={() => toggleDraftBranch(b)}
                      data-testid={`sb-branch-${b.replace(/\s+/g,'-').toLowerCase()}`}
                      className={`px-3 py-1 rounded-full text-sm border transition-colors ${sbDraft.branches.includes(b) ? 'bg-brand text-white border-brand' : 'bg-white text-slate-700 border-slate-300'}`}>
                      {b}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-700 text-sm">Installation</Label>
                  <Input data-testid="sb-installation" value={sbDraft.installation} placeholder="e.g. Fort Liberty"
                    onChange={(e) => setSbDraft(d => ({ ...d, installation: e.target.value }))} className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-slate-700 text-sm">Provider</Label>
                  <Input data-testid="sb-provider" value={sbDraft.provider} placeholder="e.g. First Command"
                    onChange={(e) => setSbDraft(d => ({ ...d, provider: e.target.value }))} className="mt-1.5" />
                </div>
              </div>
            </div>
            <DialogFooter className="mt-4 flex-row justify-between sm:justify-between">
              <Button variant="ghost" onClick={clearSbFilters} data-testid="more-filters-clear" className="text-slate-500">Clear all</Button>
              <Button onClick={applySbFilters} data-testid="more-filters-apply" className="bg-brand hover:bg-brand-hover text-white">Apply filters</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* Results Header */}
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 mb-5">
          <div className="flex items-center gap-2" data-testid="results-count">
            <p className="text-sm font-medium text-slate-700">
              {(total || jobs.length).toLocaleString()} opportunit{(total || jobs.length) !== 1 ? 'ies' : 'y'}
            </p>
            {total > jobs.length && (
              <span className="text-xs text-slate-400" data-testid="results-showing">
                Showing 1–{jobs.length.toLocaleString()} of {total.toLocaleString()}
              </span>
            )}
            {isScored && matchState === 'ready' && <span className="text-xs bg-brand-light text-brand font-semibold px-2 py-0.5 rounded-full">Matched</span>}
            {showUpdating && (
              <span className="inline-flex items-center gap-1.5 text-xs text-slate-500 font-medium" data-testid="results-updating-indicator">
                <span className="h-3 w-3 rounded-full border-2 border-slate-400 border-t-transparent animate-spin" />
                Updating opportunities…
              </span>
            )}
            {matchState === 'preparing' && (
              <span className="inline-flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 font-medium px-2 py-0.5 rounded-full" data-testid="match-preparing-badge">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
                Personalizing matches
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <div className="hidden sm:inline-flex items-center rounded-lg border border-slate-200 bg-white p-0.5 mr-1" data-testid="view-mode-toggle">
              {[['list', ListIcon, 'List'], ['split', Columns2, 'Split'], ['map', MapIcon, 'Map']].map(([m, Icon, label]) => (
                <button key={m} onClick={() => setViewMode(m)} data-testid={`view-mode-${m}`}
                  className={`inline-flex items-center gap-1 px-2.5 h-7 rounded-md text-xs font-medium transition-colors ${viewMode === m ? 'bg-brand text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                  <Icon className="w-3.5 h-3.5" />{label}
                </button>
              ))}
            </div>
            <div className="inline-flex sm:hidden items-center rounded-lg border border-slate-200 bg-white p-0.5 mr-1" data-testid="view-mode-toggle-mobile">
              {[['list', ListIcon, 'List'], ['map', MapIcon, 'Map']].map(([m, Icon, label]) => (
                <button key={m} onClick={() => setViewMode(m)} data-testid={`view-mode-m-${m}`}
                  className={`inline-flex items-center gap-1 px-2.5 h-7 rounded-md text-xs font-medium transition-colors ${viewMode === m ? 'bg-brand text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                  <Icon className="w-3.5 h-3.5" />{label}
                </button>
              ))}
            </div>
            <ArrowUpDown className="w-3.5 h-3.5 text-slate-400" />
            <select data-testid="sort-by-select" value={sortBy} onChange={(e) => changeSort(e.target.value)}
              className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs font-medium text-slate-600 focus:outline-none focus:ring-2 focus:ring-brand/30">
              <option value="best_match">{matchState === 'preparing' ? 'Sort: Best Match (preparing…)' : 'Sort: Best Match'}</option>
              <option value="nearest_home">Nearest Home</option>
              <option value="nearest_base">Nearest Base</option>
              <option value="highest_salary">Highest Salary</option>
              <option value="newest">Newest</option>
            </select>
          </div>
        </div>

        {/* View layout: List | Split | Map (map never triggers Match scoring) */}
        <div className={viewMode === 'split' ? 'flex gap-4 items-start' : ''}>
        <div className={viewMode === 'map' ? 'hidden' : (viewMode === 'split' ? 'flex-1 min-w-0 lg:max-h-[calc(100vh-230px)] lg:overflow-y-auto pr-1' : '')}>
        {/* V2-only: clear notice when no validated Home/Base coordinates exist. Browsing still
            works (Anywhere); only nearby/radius results require a verified location. */}
        {ENGINE === 'v2' && !locAvail.home && !locAvail.base && (
          <div className="mb-4 flex items-center gap-2.5 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm text-blue-800" data-testid="v2-location-notice">
            <Info className="w-4 h-4 text-blue-500 flex-shrink-0" />
            <span>Add or verify your Home or Base location to use nearby results (radius, Nearest Home, Nearest Base). You can still browse Anywhere and use Best Match, Newest, and Highest Salary.</span>
          </div>
        )}

        {/* Match-preparing notice — list is browsable immediately; Best Match auto-loads when ready */}
        {matchState === 'preparing' && (
          <div className="mb-4 flex items-center gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800" data-testid="match-preparing-banner">
            <div className="h-4 w-4 rounded-full border-2 border-amber-500 border-t-transparent animate-spin flex-shrink-0" />
            <span>{filters.opportunity_type === 'skillbridge'
              ? (matchStalled
                  ? 'Calculating Match scores for SkillBridge opportunities — this is taking a little longer. You can keep browsing now.'
                  : 'Adding SkillBridge opportunities to your matches. Calculating Match scores — Best Match will load automatically when it’s ready.')
              : (matchStalled
                  ? 'Personalized matches are taking longer than expected. You can continue browsing opportunities now.'
                  : 'Personalizing your matches — they refresh automatically after Career Profile changes. You can browse opportunities now; Best Match will load automatically when it’s ready.')}</span>
          </div>
        )}
        {/* SkillBridge matching not enabled — user asked to rank SkillBridge by Match while the
            source is off. Truthful prompt to enable (no fake scores, no blocking). */}
        {matchState === 'skillbridge_match_disabled' && (
          <div className="mb-4 flex items-start gap-2.5 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700" data-testid="skillbridge-match-disabled-banner">
            <Info className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
            <span>Turn on <span className="font-semibold">SkillBridge matching</span> above to rank these opportunities by Match. You can keep browsing them by Newest in the meantime.</span>
          </div>
        )}
        {(matchState === 'failed' || matchState === 'incomplete' || matchState === 'unavailable') && (
          <div className="mb-4 flex items-center gap-2.5 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-600" data-testid="match-unavailable-banner">
            <Info className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <span>Match is temporarily unavailable. You can continue browsing opportunities.</span>
          </div>
        )}

        {/* Job Listings */}
        {loading && jobs.length === 0 ? (
          <div className="space-y-3" data-testid="jobs-loading-skeleton">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white rounded-xl border border-slate-200 p-5 animate-pulse">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0 space-y-2.5">
                    <div className="h-5 bg-slate-200 rounded w-1/2" />
                    <div className="flex gap-3">
                      <div className="h-3 bg-slate-100 rounded w-24" />
                      <div className="h-3 bg-slate-100 rounded w-20" />
                    </div>
                    <div className="h-3 bg-slate-100 rounded w-3/4" />
                    <div className="h-3 bg-slate-100 rounded w-2/3" />
                  </div>
                  <div className="h-8 w-8 bg-slate-100 rounded-full flex-shrink-0" />
                </div>
              </div>
            ))}
          </div>
        ) : jobs.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-white p-16 text-center" data-testid="opportunities-empty-state">
            {filters.saved_only ? (
              <>
                <Heart className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600 font-medium">No saved opportunities yet.</p>
                <p className="text-sm text-slate-400 mt-1">Tap the heart on any opportunity to save it, then find it here anytime.</p>
              </>
            ) : (
              <>
                <SlidersHorizontal className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600 font-medium">No roles match this view yet. Try widening your radius.</p>
                <p className="text-sm text-slate-400 mt-1">Try adjusting your search criteria</p>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {jobs.map((job) => {
              const isSb = job.opportunity_type === 'skillbridge';
              const sb = isSb ? skillbridgeDisplay(job) : null;
              const cardTitle = isSb ? sb.displayTitle : job.title;
              const cardSummary = isSb ? sb.summary : job.description;
              return (
              <div
                key={job.job_id}
                ref={el => { cardRefs.current[job.job_id] = el; }}
                role="button"
                tabIndex={0}
                aria-pressed={selectedJobId === job.job_id}
                className={`group bg-white rounded-xl border ${selectedJobId === job.job_id ? 'border-brand ring-2 ring-brand/30' : 'border-slate-200'} hover:border-brand/40 hover:shadow-md transition-all p-5 cursor-pointer`}
                onClick={() => { setSelectedJobId(job.job_id); openPreview(job.job_id); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedJobId(job.job_id); openPreview(job.job_id); } }}
                onDoubleClick={() => navigate(`/jobs/${job.job_id}`)}
                data-testid={`job-card-${job.job_id}`}
              >
                <div className="flex items-start gap-4">
                  {/* Match shown as the compact pill beside the title (single presentation) */}

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2.5 mb-1.5">
                          <h3 className="text-lg font-semibold text-slate-900 truncate w-full sm:w-auto sm:flex-1 min-w-0 group-hover:text-brand transition-colors" data-testid={`job-title-${job.job_id}`}>{cardTitle}</h3>
                          {job.opportunity_type === 'skillbridge' && (
                            <span data-testid={`skillbridge-badge-${job.job_id}`} className="px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 text-[11px] font-semibold uppercase tracking-wide flex-shrink-0">SkillBridge</span>
                          )}
                          {matchReady && job.match_score != null && (
                            <span className="inline-flex items-center gap-1 flex-shrink-0" data-testid={`match-pill-${job.job_id}`}>
                              <span className="text-[11px] font-semibold text-brand bg-brand-light px-2 py-0.5 rounded-full tabular-nums">Match {formatMatch(job.match_score)}</span>
                              <MatchInfo />
                            </span>
                          )}
                        </div>
                        {isSb && sb.showProvider && (
                          <p className="text-sm font-medium text-slate-700 mb-1" data-testid={`sb-provider-${job.job_id}`}>{sb.provider}</p>
                        )}
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500 mb-2.5">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5" />
                            {job.remote ? 'Remote' : job.location}
                          </span>
                          {isSb && sb.showProgramType && (
                            <span className="flex items-center gap-1" data-testid={`sb-program-type-${job.job_id}`}>
                              <Briefcase className="w-3.5 h-3.5" />
                              {sb.programType}
                            </span>
                          )}
                          {!isSb && job.job_type && (
                            <span className="flex items-center gap-1">
                              <Briefcase className="w-3.5 h-3.5" />
                              {job.job_type.replace('_', ' ')}
                            </span>
                          )}
                          {(job.salary_min || job.salary_max) && (
                            <span className="flex items-center gap-1 font-medium text-slate-700">
                              <DollarSign className="w-3.5 h-3.5" />
                              {job.salary_min && `$${(job.salary_min/1000).toFixed(0)}k`}
                              {job.salary_min && job.salary_max && '–'}
                              {job.salary_max && `$${(job.salary_max/1000).toFixed(0)}k`}
                            </span>
                          )}
                        </div>
                      </div>
                      {user && (
                        <button
                          data-testid={`favorite-btn-${job.job_id}`}
                          onClick={(e) => { e.stopPropagation(); toggleFavorite(job.job_id); }}
                          className="p-2 hover:bg-slate-100 rounded-full transition-colors flex-shrink-0"
                        >
                          <Heart className={`w-5 h-5 ${favorites.has(job.job_id) ? 'fill-red-500 text-red-500' : 'text-slate-300 group-hover:text-slate-400'}`} />
                        </button>
                      )}
                      <PinJobButton jobId={job.job_id} />
                    </div>

                    {cardSummary && <p className="text-sm text-slate-600 line-clamp-2 mb-3">{cardSummary}</p>}

                    {/* Phase 1B — region context line (client-side bucketed) */}
                    {!job.remote && (
                      <RegionContextLine
                        distanceMiles={job.distance_to_candidate_home_miles}
                        city={job.location}
                        remote={job.remote}
                        testId={`job-card-region-context-${job.job_id}`}
                        className="mb-2"
                      />
                    )}

                    {/* External source attribution */}
                    {job.source_type === 'external' && (
                      <div className="flex items-center gap-3 mb-2 text-xs text-slate-400">
                        <span>Source: {job.source_name || 'External'}</span>
                        {job.canonical_url && (
                          <a href={job.canonical_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-brand hover:underline">View original posting</a>
                        )}
                      </div>
                    )}

                    {/* Explanation + Distance */}
                    <div className="flex flex-wrap items-center gap-3">
                      <ExplanationChips tags={(() => {
                        const t = job.explanation?.tags || [];
                        if (job.remote && !t.includes('remote_friendly')) return ['remote_friendly', ...t];
                        return t;
                      })()} max={3} />
                      {!job.remote && job.distance_to_candidate_home_miles != null && (
                        <span className="text-[11px] text-slate-400">{Math.round(job.distance_to_candidate_home_miles)} mi from home</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}

        {!loading && jobs.length > 0 && hasMore && (
          <div className="flex flex-col items-center mt-6 gap-2">
            <Button
              data-testid="load-more-btn"
              onClick={loadMore}
              disabled={loadingMore}
              variant="outline"
              className="h-11 px-8 border-slate-300 text-slate-700"
            >
              {loadingMore ? 'Loading\u2026' : `Load ${Math.min(PAGE_SIZE, Math.max(0, total - jobs.length))} more`}
            </Button>
            {total > 0 && (
              <p className="text-xs text-slate-400" data-testid="load-more-status">
                Showing 1&ndash;{jobs.length.toLocaleString()} of {total.toLocaleString()}
              </p>
            )}
          </div>
        )}
        </div>{/* end list column */}
        {viewMode !== 'list' && (
          <div className={viewMode === 'map'
              ? 'w-full h-[calc(100vh-230px)]'
              : 'w-[43%] flex-shrink-0 h-[calc(100vh-230px)] sticky top-4'}
            data-testid="opportunities-map-panel">
            <OpportunitiesMap
              visible={viewMode !== 'list'}
              jobsSelected={jobsSelected}
              sbSelected={sbSelected}
              opportunityType={filters.opportunity_type}
              remote={filters.remote}
              jobType={filters.job_type}
              savedOnly={filters.saved_only}
              savedNonce={savedNonce}
              initialCenter={homeCoords || baseCoords}
              focus={mapFocus}
              selectedJobId={selectedJobId}
              onSelectJob={setSelectedJobId}
              onOpenJob={openPreview}
            />
          </div>
        )}
        </div>{/* end view layout */}
      </div>

      {preview && (
        <OpportunityPreview
          jobId={preview.jobId}
          user={user}
          resume={resume}
          matchScore={preview.matchScore}
          explanationTags={preview.listJob?.explanation?.tags}
          distanceMiles={preview.listJob?.distance_to_candidate_home_miles}
          isFavorite={favorites.has(preview.jobId)}
          onToggleFavorite={() => toggleFavorite(preview.jobId)}
          onClose={() => { setPreview(null); setSelectedJobId(null); }}
          onOpenFull={(id) => navigate(`/jobs/${id}`)}
        />
      )}
    </Layout>
  );
}

export default JobBoard;
